import React, { useEffect, useState } from "react";

import { useConfig } from "../../context/ConfigContext.js";
import { useLocation } from "react-router-dom";

import "./RafflePrizeWidget.css";

export default function RafflePrizeWidget() {
  const { config } = useConfig();
  const location = useLocation();
  
  const [open, setOpen] = useState(false);

  const [prizes, setPrizes] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const apiBase = (config?.apiUrl || "/api").replace(/\/$/, "");

  useEffect(() => {
    setOpen(false);
}, [location.pathname, location.search]);

  useEffect(() => {
    if (!config) return;

    const controller = new AbortController();

    async function loadPrizes() {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(
          `${apiBase}/raffle/prizes`,
          {
            signal: controller.signal,
            cache: "no-store",
          }
        );

        if (!res.ok) {
          throw new Error("Unable to load raffle prizes.");
        }

        const data = await res.json();

        setPrizes(data.prizes || []);
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadPrizes();

    return () => controller.abort();
  }, [apiBase, config]);

  const filteredPrizes = prizes.filter((prize) => {
    const text = [
      prize.name,
      prize.description,
      prize.donatedBy,
    ]
      .join(" ")
      .toLowerCase();

    return text.includes(search.toLowerCase());
  });

  return (
    <div className="bb3-raffle-widget">
      {open && (
        <section
          className="bb3-raffle-panel"
          aria-label="Raffle prizes"
        >
          <div className="bb3-raffle-header">
            <div>
              <h3>🎟️ Raffle Prizes</h3>

              <p>Take a look at what you could win!</p>
            </div>

            <button
              type="button"
              className="bb3-raffle-close"
              onClick={() => setOpen(false)}
              aria-label="Collapse raffle prizes"
            >
              ▼
            </button>
          </div>

          <div className="bb3-raffle-body">
            <input
              type="search"
              placeholder="Search prizes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bb3-raffle-search"
            />

            <div className="bb3-raffle-list">
              {loading && <p>Loading prizes...</p>}

              {error && (
                <p className="bb3-raffle-error">{error}</p>
              )}

              {!loading &&
                !error &&
                filteredPrizes.length === 0 && (
                  <p>No prizes found.</p>
                )}

              {!loading &&
                !error &&
                filteredPrizes.map((prize) => (
                  <article
                    className="bb3-raffle-prize"
                    key={prize._id}
                  >
                    {prize.imageUrl && (
                      <img
                        src={prize.imageUrl}
                        alt={prize.name}
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}

                    <div className="bb3-raffle-prize-info">
                      <h4>{prize.name}</h4>

                      {prize.description && (
                        <p>{prize.description}</p>
                      )}

                      {prize.donatedBy && (
                        <span>
                          Donated by {prize.donatedBy}
                        </span>
                      )}
                    </div>
                  </article>
                ))}
            </div>

            <a
              href="https://checkout.square.site/merchant/MLW363JJ1PP43/checkout/YOJ4GOZWSQWFJRN7W34WGSWT?src=sheet"
              className="bb3-raffle-enter"
            >
              Enter the Raffle →
            </a>
          </div>
        </section>
      )}

      <button
        type="button"
        className="bb3-raffle-toggle"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
      >
        <span>🎟️ {open ? "Hide Prizes" : "View Raffle Prizes"}</span>

        <span>{open ? "▼" : "▲"}</span>
      </button>
    </div>
  );
}